import React, { useEffect, useState } from 'react';
import { FeatureConfigService } from '../services/featureConfigService';
import { CircularProgress, Box, Typography } from '@mui/material';
import { useAuth } from '../../features/auth/hooks/useAuth';

interface FeatureProviderProps {
    children: React.ReactNode;
    loadingComponent?: React.ReactNode;
    errorComponent?: React.ReactNode;
}

const FeatureProvider: React.FC<FeatureProviderProps> = ({
    children,
    loadingComponent,
    errorComponent,
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const { tokens } = useAuth();

    useEffect(() => {
        const service = FeatureConfigService.getInstance();

        // Only load features if user is authenticated
        if (tokens) {
            service
                .loadFeatures()
                .then(() => {
                    setIsLoading(false);
                })
                .catch((_) => {
                    setHasError(true);
                    setIsLoading(false);
                });
        } else {
            // If not authenticated, skip loading features and continue with defaults
            setIsLoading(false);
            setHasError(false);
        }
    }, [tokens]);

    if (isLoading) {
        return (
            loadingComponent || (
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="100vh"
                    gap={2}
                >
                    <CircularProgress />
                    <Typography variant="body2" color="textSecondary">
                        Laddar funktioner...
                    </Typography>
                </Box>
            )
        );
    }

    if (hasError) {
        return (
            errorComponent || (
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="100vh"
                    gap={2}
                >
                    <Typography variant="h6" color="error">
                        Kunde inte ladda funktioner
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Applikationen fortsätter med standardinställningar
                    </Typography>
                </Box>
            )
        );
    }

    return <>{children}</>;
};

export default FeatureProvider;
